import { Home, FileText, Mail, FolderOpen, Linkedin, Briefcase, CheckCircle, User, LogOut, Sparkles as SparklesAlt, Target, Search, Zap, MessageSquare, Bot, Building2, Sparkles, ChevronDown, DollarSign, HelpCircle, Shield } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { CreditsBadge } from "@/components/CreditsBadge";
import { useSubscription } from "@/hooks/useSubscription";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useJobSearchPanel } from "@/contexts/JobSearchPanelContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useState } from "react";

const mainItems = [
  { title: "Dashboard", url: "/app", icon: Home, exact: true },
  { title: "Resume Library", url: "/app/resume/library", icon: FileText },
  { title: "Create Cover Letter", url: "/app/letters", icon: Mail },
  { title: "My Documents", url: "/app/documents", icon: FolderOpen },
  { title: "Application Tracker", url: "/app/application-tracker", icon: Briefcase },
];

const proItems = [
  { title: "Job Analyzer", url: "/app/job-analyzer", icon: Target, feature: "jobAnalyzer" as const },
  { title: "ATS Score Checker", url: "/app/ats-score-checker", icon: CheckCircle, feature: "atsScoreChecker" as const },
  { title: "LinkedIn Optimizer", url: "/app/linkedin-optimizer", icon: Linkedin, feature: "linkedinOptimizer" as const },
  { title: "Salary Negotiation", url: "/app/salary-negotiation", icon: DollarSign, feature: "salaryNegotiation" as const },
];

const premiumItems = [
  { title: "Smart Apply", url: "/app/smart-apply/queue", icon: Sparkles, feature: "smartApply" as const },
  { title: "Company Insights", url: "/app/company-insights", icon: Building2, feature: "companyInsights" as const },
  { title: "AI Interview Prep", url: "/app/ai-interview-prep", icon: MessageSquare, feature: "aiInterviewPrep" as const },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasFeature, subscription, loading: subscriptionLoading } = useSubscription();
  const { isAdmin } = useIsAdmin();
  const { openPanel } = useJobSearchPanel();
  const collapsed = state === "collapsed";
  const [proOpen, setProOpen] = useState(true);
  const [premiumOpen, setPremiumOpen] = useState(true);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const getNavCls = (active: boolean) =>
    active 
      ? "bg-gradient-to-r from-primary/15 to-accent/10 text-primary font-semibold border-l-2 border-primary hover:from-primary/20 hover:to-accent/15" 
      : "hover:bg-muted/50 hover:border-l-2 hover:border-muted-foreground/20";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b p-4 bg-gradient-to-br from-primary/5 to-accent/5">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-bold text-lg bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              LoopBaan.ai
            </span>
          </div>
        )}
        {collapsed && <Logo className="h-8 w-8 mx-auto" />}
      </SidebarHeader>

      <SidebarContent className="px-2">
        {/* New Action Button */}
        {!collapsed && (
          <div className="p-2">
            <Button className="w-full gap-2 shadow-lg bg-gradient-to-r from-primary to-accent hover:shadow-xl transition-all" size="lg" asChild>
              <NavLink to="/app/resume">
                <Sparkles className="h-4 w-4" />
                New Resume
              </NavLink>
            </Button>
          </div>
        )}

        {/* Main Navigation */}
        <SidebarGroup>
          {!collapsed && <SidebarGroupLabel>Main Menu</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.exact}
                      className={({ isActive }) => getNavCls(isActive)}
                    >
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {/* Job Search - navigates to job search page */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/app/job-search"
                    className={({ isActive }) => getNavCls(isActive)}
                  >
                    <Search className="h-4 w-4" />
                    {!collapsed && <span>Job Search</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <Separator className="my-2" />

        {/* Pro Features */}
        <Collapsible open={proOpen} onOpenChange={setProOpen}>
          <SidebarGroup>
            {!collapsed && (
              <HoverCard openDelay={200}>
                <HoverCardTrigger asChild>
                  <CollapsibleTrigger className="w-full">
                    <SidebarGroupLabel className="flex items-center justify-between cursor-pointer hover:bg-muted/50 rounded-md px-2 py-1 transition-colors">
                      <span className="flex items-center gap-2">
                        Pro Features
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${proOpen ? '' : '-rotate-90'}`} />
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                </HoverCardTrigger>
                <HoverCardContent side="right" className="w-64 p-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Zap className="h-4 w-4 text-primary" />
                      Pro Features
                    </h4>
                    <div className="space-y-2 text-sm">
                      {proItems.map((item) => (
                        <div key={item.title} className="flex items-center gap-2 text-muted-foreground">
                          <item.icon className="h-3 w-3" />
                          <span>{item.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            )}
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {proItems.map((item) => {
                    const hasAccess = hasFeature(item.feature);
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild disabled={!hasAccess}>
                          <NavLink
                            to={hasAccess ? item.url : "/pricing"}
                            className={({ isActive }) => 
                              `${getNavCls(isActive)} ${!hasAccess ? 'opacity-50' : ''}`
                            }
                          >
                            <item.icon className="h-4 w-4" />
                            {!collapsed && (
                              <span className="flex items-center justify-between w-full">
                                {item.title}
                                {!subscriptionLoading && !hasAccess && <Badge variant="outline" className="text-xs ml-2">Pro</Badge>}
                              </span>
                            )}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Separator className="my-2" />

        {/* Premium Features */}
        <Collapsible open={premiumOpen} onOpenChange={setPremiumOpen}>
          <SidebarGroup>
            {!collapsed && (
              <HoverCard openDelay={200}>
                <HoverCardTrigger asChild>
                  <CollapsibleTrigger className="w-full">
                    <SidebarGroupLabel className="flex items-center justify-between cursor-pointer hover:bg-muted/50 rounded-md px-2 py-1 transition-colors">
                      <span className="flex items-center gap-2">
                        Premium Features
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${premiumOpen ? '' : '-rotate-90'}`} />
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                </HoverCardTrigger>
                <HoverCardContent side="right" className="w-64 p-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Premium Features
                    </h4>
                    <div className="space-y-2 text-sm">
                      {premiumItems.map((item) => (
                        <div key={item.title} className="flex items-center gap-2 text-muted-foreground">
                          <item.icon className="h-3 w-3" />
                          <span>{item.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            )}
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {premiumItems.map((item) => {
                    const hasAccess = hasFeature(item.feature);
                    
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild disabled={!hasAccess}>
                          <NavLink
                            to={hasAccess ? item.url : "/pricing"}
                            className={({ isActive }) => 
                              `${getNavCls(isActive)} ${!hasAccess ? 'opacity-50' : ''}`
                            }
                          >
                            <item.icon className="h-4 w-4" />
                            {!collapsed && (
                              <span className="flex items-center justify-between w-full">
                                {item.title}
                                {!subscriptionLoading && !hasAccess && <Badge variant="outline" className="text-xs ml-2">Premium</Badge>}
                              </span>
                            )}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Separator className="my-2" />

        {/* Admin Section - Only visible to admins */}
        {isAdmin && (
          <>
            <SidebarGroup>
              {!collapsed && (
                <SidebarGroupLabel className="flex items-center gap-2 text-primary">
                  <Shield className="h-4 w-4" />
                  Admin
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to="/app/admin"
                        className={({ isActive }) => getNavCls(isActive)}
                      >
                        <Shield className="h-4 w-4" />
                        {!collapsed && <span>Admin Panel</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <Separator className="my-2" />
          </>
        )}

        {/* Account Section */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/app/account"
                    className={({ isActive }) => getNavCls(isActive)}
                  >
                    <User className="h-4 w-4" />
                    {!collapsed && <span>Account</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/help"
                    className={({ isActive }) => getNavCls(isActive)}
                  >
                    <HelpCircle className="h-4 w-4" />
                    {!collapsed && <span>Help & Support</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t p-2 bg-gradient-to-br from-card to-card/50">
        {!collapsed && (
          <div className="mb-2 px-2">
            <CreditsBadge />
          </div>
        )}
        <Button
          variant="ghost"
          className="w-full justify-start hover:bg-destructive/10 hover:text-destructive transition-colors"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && <span className="ml-2">Log Out</span>}
        </Button>
        
      </SidebarFooter>
    </Sidebar>
  );
}
